import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/stores';
import { useLoading } from './useLoading';
import { authApi } from '@/features/auth';

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    clearAuth,
    isInitializing,
    setInitializing,
    setUser,
    setTokenAuth,
    user,
  } = useAuthStore();
  const { setLoading } = useLoading();

  const isUserAuthenticated = isAuthenticated();

  // React Query Mutation for checking user auth
  const checkUserMutation = useMutation({
    mutationFn: () => authApi.checkUser(),
    retry: false, // 認證失敗不重試
    onSuccess: (response) => {
      // 認證成功，更新用戶資料
      setUser(response);
      setInitializing(false);
      setLoading(false);
    },
    onError: (error: Error) => {
      // Token 過期或無效，清除認證狀態
      console.error('認證檢查失敗:', error);
      clearAuth();
      setInitializing(false);
      setLoading(false);
      navigate('/login', { replace: true });
    },
  });

  // 初始化認證狀態 - 檢查用戶認證是否有效
  useEffect(() => {
    if (isInitializing) {
      if (isUserAuthenticated) {
        setLoading(true);
        checkUserMutation.mutate();
      } else {
        // 沒有 token，直接完成初始化
        setInitializing(false);
        setLoading(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: Authentication redirect is handled at the route level in App.tsx
  // Semi-public routes (landing, ai-chat) allow guest access with feature gates

  // 登出 mutation
  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      // API 成功後才清除認證狀態並重定向
      clearAuth();
      navigate('/login', { replace: true });
    },
    onError: (error: Error) => {
      // 登出失敗的處理（可選）
      console.error('登出失敗:', error);
      // 即使 API 失敗，也可以選擇清除本地狀態
      clearAuth();
      navigate('/login', { replace: true });
    },
  });

  return {
    // Store 狀態和方法
    isAuthenticated,
    clearAuth,
    isInitializing,
    setInitializing,
    setUser,
    setTokenAuth,
    user,

    // 業務邏輯
    isUserAuthenticated,
    checkUserMutation,
    logout: () => logoutMutation.mutate(),
  };
};
